import { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EchoContext from 'context/EchoContext';
import authSlice from 'store/modules/auth';
import clientPaymentSlice from 'store/modules/clientPayment';
import { createSnack } from 'common/utils/general';
import { roles } from 'common/constants/options';
import uiSlice from 'store/modules/ui';
import { useTranslation } from 'react-i18next';

/**
 * Hook to listen for payment events via WebSocket
 *
 * Features:
 * - Listens 24/7 for payment.completed and payment.deleted events
 * - Works independently of payment modal (background listener)
 * - Updates user membership status (inactive ↔ active)
 * - Shows persistent notifications only when modal is NOT actively processing
 * - Automatically refreshes Redux state without page reload
 * - Bilingual support (ES/EN)
 *
 * This hook should be integrated at the App level to ensure constant listening
 */
function usePaymentListener() {
  const { t } = useTranslation(['notifications']);
  const dispatch = useDispatch();
  const echoContext = useContext(EchoContext);
  const echo = echoContext?.echo || null;
  const userId = useSelector((state) => state.auth.user?.id);
  const userRole = useSelector((state) => state.auth.user?.role);
  const isClient = userRole === roles.client;
  const isModalProcessing = useSelector((state) => state.ui.isPaymentModalProcessing);
  const channelRef = useRef(null);
  const isModalProcessingRef = useRef(isModalProcessing);
  const previousUserIdRef = useRef(userId);

  // Mantener la ref actualizada
  useEffect(() => {
    isModalProcessingRef.current = isModalProcessing;
  }, [isModalProcessing]);

  // Detectar cambio de usuario y limpiar listener del usuario anterior
  useEffect(() => {
    if (previousUserIdRef.current !== userId && previousUserIdRef.current !== null) {
      // Usuario cambió - limpiar listener del usuario anterior
      if (channelRef.current && echo && typeof echo.leave === 'function') {
        try {
          channelRef.current.stopListening('.payment.completed');
          channelRef.current.stopListening('.payment.deleted');
          echo.leave(`users.${previousUserIdRef.current}`);
          channelRef.current = null;
        } catch (error) {
          // Silent cleanup
        }
      }
    }
    previousUserIdRef.current = userId;
  }, [userId, echo]);

  useEffect(() => {
    // Solo los clientes generan eventos de pago en su canal privado; admin/trainer no.
    if (!echo || !userId || !isClient) {
      return undefined;
    }

    // Prevent double subscription
    if (channelRef.current) {
      return undefined;
    }

    // Double-check echo is valid before subscribing (prevents race conditions)
    if (!echo.private || typeof echo.private !== 'function') {
      return undefined;
    }

    // Subscribe to the user's private channel
    let channel;
    try {
      channel = echo.private(`users.${userId}`);
      channelRef.current = channel;
    } catch (error) {
      return undefined;
    }

    // Listen for payment.completed event from Stripe webhook
    channel.listen('.payment.completed', (eventData) => {
      // Update Redux directly with the data from the event
      if (eventData?.membership_status || eventData?.sessions_paid) {
        dispatch(authSlice.actions.meSuccess({
          membership_status: eventData.membership_status,
          available_sessions: eventData.sessions_paid
        }));
      }

      // Trigger payment table refresh without page reload
      dispatch(clientPaymentSlice.actions.removeSuccess());

      // Show notification ONLY if modal is not actively processing
      // (if modal is processing, it will show its own success state)
      if (!isModalProcessingRef.current) {
        const snack = createSnack({
          key: `payment-completed-${Date.now()}`,
          variant: 'success',
          message: t('payment.completed', { ns: 'notifications' }),
          options: {
            persist: true,
            preventDuplicate: false
          }
        });
        dispatch(uiSlice.actions.enqueueSnackbar(snack));
      }
    });

    // Listen for payment.deleted event from admin panel
    channel.listen('.payment.deleted', (eventData) => {
      // Update Redux directly with the new user state
      if (eventData?.membership_status !== undefined) {
        dispatch(authSlice.actions.meSuccess({
          membership_status: eventData.membership_status,
          available_sessions: eventData.available_sessions ?? 0
        }));
      }

      // Trigger payment table refresh without page reload
      dispatch(clientPaymentSlice.actions.removeSuccess());

      // No notification - silent update for better UX
    });

    // Cleanup on unmount or when dependencies change
    return () => {
      if (channelRef.current && echo && typeof echo.leave === 'function') {
        try {
          channelRef.current.stopListening('.payment.completed');
          channelRef.current.stopListening('.payment.deleted');
          echo.leave(`users.${userId}`);
          channelRef.current = null;
        } catch (error) {
          // Silent cleanup
        }
      }
    };
  }, [echo, userId, isClient, dispatch, t]);
}

export default usePaymentListener;
